<script setup>
import { ref, watch, onMounted } from 'vue';

// Editor visual minimalista para email marketing. Proposital:
// - contenteditable puro (sem ProseMirror, sem file input escondido,
//   sem upload de imagem nenhum)
// - 5 botoes: Negrito, Italico, Link, Lista, Lista numerada
// - v-model em HTML
// API legada execCommand: funciona em todos browsers atuais; nao tem
// substituto cross-browser tao simples pra essa formatacao basica.
const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const editor = ref(null);
const internalValue = ref(props.modelValue || '');

const syncOut = () => {
  if (!editor.value) return;
  const html = editor.value.innerHTML;
  if (html !== internalValue.value) {
    internalValue.value = html;
    emit('update:modelValue', html);
  }
};

const setHtml = html => {
  if (!editor.value) return;
  if (editor.value.innerHTML === html) return;
  editor.value.innerHTML = html || '';
};

watch(
  () => props.modelValue,
  v => setHtml(v || ''),
  { immediate: false }
);

onMounted(() => {
  setHtml(props.modelValue || '');
});

const exec = command => {
  document.execCommand(command, false, null);
  if (editor.value) editor.value.focus();
  syncOut();
};

const insertLink = () => {
  // eslint-disable-next-line no-alert
  const url = window.prompt('URL do link (https://...)');
  if (!url) return;
  document.execCommand('createLink', false, url);
  syncOut();
};
</script>

<template>
  <div class="border border-n-weak rounded-lg bg-n-background">
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <div class="flex items-center gap-0.5 px-2 py-1.5 border-b border-n-weak">
      <button
        type="button"
        class="p-1.5 rounded hover:bg-n-slate-3 text-n-slate-12 font-bold text-xs w-7"
        title="Negrito (Ctrl+B)"
        @click="exec('bold')"
      >
        B
      </button>
      <button
        type="button"
        class="p-1.5 rounded hover:bg-n-slate-3 text-n-slate-12 italic text-xs w-7"
        title="Itálico (Ctrl+I)"
        @click="exec('italic')"
      >
        I
      </button>
      <button
        type="button"
        class="p-1.5 rounded hover:bg-n-slate-3 text-n-slate-12 w-7 flex items-center justify-center"
        title="Link"
        @click="insertLink"
      >
        <span class="i-lucide-link size-3.5" />
      </button>
      <span class="w-px h-4 bg-n-weak mx-1" />
      <button
        type="button"
        class="p-1.5 rounded hover:bg-n-slate-3 text-n-slate-12 w-7 flex items-center justify-center"
        title="Lista"
        @click="exec('insertUnorderedList')"
      >
        <span class="i-lucide-list size-3.5" />
      </button>
      <button
        type="button"
        class="p-1.5 rounded hover:bg-n-slate-3 text-n-slate-12 w-7 flex items-center justify-center"
        title="Lista numerada"
        @click="exec('insertOrderedList')"
      >
        <span class="i-lucide-list-ordered size-3.5" />
      </button>
    </div>
    <!-- eslint-enable vue/no-bare-strings-in-template -->
    <div
      ref="editor"
      contenteditable="true"
      class="prose prose-sm max-w-none px-3 py-2 min-h-[200px] text-sm text-n-slate-12 focus:outline-none"
      :data-placeholder="placeholder"
      @input="syncOut"
      @blur="syncOut"
    />
  </div>
</template>

<style scoped>
[contenteditable]:empty::before {
  content: attr(data-placeholder);
  color: rgb(var(--colors-n-slate-9) / 1);
  pointer-events: none;
}
[contenteditable] ul {
  list-style: disc;
  padding-left: 1.5rem;
}
[contenteditable] ol {
  list-style: decimal;
  padding-left: 1.5rem;
}
[contenteditable] a {
  color: rgb(var(--colors-woot-500) / 1);
  text-decoration: underline;
}
</style>
